import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import HeroTicker from "@/components/HeroTicker";

const HAKAN_IMG =
  "https://customer-assets-jai6qajn.emergentagent.net/job_hakan-analytics/artifacts/zp3kj875_ChatGPT%20Image%20Aug%203%2C%202026%2C%2011_52_58%20AM.png";

const STADIUM_BG =
  "https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const lineChild = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] } },
};

const Hero = () => {
  const { t } = useLang();
  const navigate = useNavigate();

  const scrollToPlans = () => {
    document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToArchive = () => {
    navigate("/archives");
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden"
    >
      {/* parallax stadium background */}
      <motion.div
        aria-hidden
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.25 }}
        transition={{ duration: 2 }}
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: `url(${STADIUM_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(70%) contrast(1.1)",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/50 via-black/70 to-black" />

      {/* red vertical accent */}
      <div className="absolute left-0 top-1/4 w-[2px] h-40 bg-[var(--hk-red)]" />
      <div className="absolute right-0 top-2/3 w-[2px] h-24 bg-white/20" />

      <div className="container-hk relative">
        {/* Overline + Football ticker row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 items-center mb-8 md:mb-12"
        >
          <div
            className="lg:col-span-7 flex items-center gap-3"
            data-testid="hero-overline"
          >
            <span className="w-2 h-2 bg-[var(--hk-red)] animate-pulse" />
            <span className="text-[10px] md:text-xs tracking-[0.35em] text-white/70 font-semibold">
              {t.hero.overline}
            </span>
          </div>
          <div className="lg:col-span-5">
            <HeroTicker />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Title */}
          <div className="lg:col-span-7 relative">
            <motion.h1
              variants={lineParent}
              initial="hidden"
              animate="visible"
              className="relative z-10 font-display text-[13vw] sm:text-[10vw] md:text-[8vw] lg:text-[6.5vw] xl:text-[6vw] leading-[0.92] tracking-tight uppercase"
              data-testid="hero-title"
            >
              <span className="reveal-mask">
                <motion.span variants={lineChild} className="block">
                  {t.hero.title_line_1}
                </motion.span>
              </span>
              <span className="reveal-mask">
                <motion.span
                  variants={lineChild}
                  className="block text-[var(--hk-red)]"
                >
                  {t.hero.title_line_2}
                </motion.span>
              </span>
              <span className="reveal-mask">
                <motion.span variants={lineChild} className="block text-white/85">
                  {t.hero.title_line_3}
                </motion.span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 md:mt-10 text-sm md:text-base text-white/70 leading-relaxed max-w-xl"
              data-testid="hero-lede"
            >
              {t.hero.lede}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.7 }}
              className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
            >
              <button
                data-testid="hero-cta-buy"
                onClick={scrollToPlans}
                className="btn-red px-7 py-4 text-xs font-bold tracking-[0.28em] w-full sm:w-auto"
              >
                {t.hero.cta_primary} →
              </button>
              <button
                data-testid="hero-cta-archive"
                onClick={scrollToArchive}
                className="btn-outline px-7 py-4 text-xs font-bold tracking-[0.28em] w-full sm:w-auto"
              >
                {t.hero.cta_secondary}
              </button>
            </motion.div>
          </div>

          {/* Right: Winning celebration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-5 relative"
            data-testid="hero-portrait"
          >
            <div className="relative aspect-[4/5] w-full max-w-sm mx-auto lg:max-w-none">
              {/* frame */}
              <div className="absolute -inset-2 md:-inset-3 border border-white/15" />
              <div className="absolute -bottom-3 -right-3 w-24 h-24 md:w-32 md:h-32 border-2 border-[var(--hk-red)]" />
              <div className="relative w-full h-full overflow-hidden bg-neutral-900">
                <motion.img
                  src={HAKAN_IMG}
                  alt="Members celebrating winning betting slips"
                  className="w-full h-full object-cover"
                  style={{ filter: "contrast(1.05) saturate(1.05)" }}
                  initial={{ scale: 1.15 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 2.5, ease: [0.19, 1, 0.22, 1] }}
                />
                {/* red spotlight */}
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 30%, rgba(227,10,23,0.28), transparent 55%)",
                  }}
                />
                {/* Turkish flag accent chip */}
                <div className="absolute top-3 right-3 md:top-4 md:right-4" aria-label="Türkiye">
                  <svg width="34" height="23" viewBox="0 0 300 200" className="rounded-[2px] shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                    <rect width="300" height="200" fill="#E30A17" />
                    <circle cx="120" cy="100" r="45" fill="#fff" />
                    <circle cx="132" cy="100" r="36" fill="#E30A17" />
                    <polygon points="188,100 200,82 220,82 205,96 212,116 188,104 164,116 171,96 156,82 176,82" fill="#fff" />
                  </svg>
                </div>
                {/* badge overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 bg-gradient-to-t from-black via-black/70 to-transparent">
                  <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-1">
                    {t.hero.badge_founder}
                  </div>
                  <div className="font-display text-lg md:text-xl tracking-[0.05em] text-white">
                    {t.hero.badge_name}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
